import { YesNoConfirmModal } from "@common/components/modals";
import { useApp, usePetDetailsStore } from "@common/hooks";
import { DiagnosisModel, PetDiagnosisModel } from "@common/models";
import { StringUtility, makeBold } from "@common/utils";
import {
  PetProfileSearchInitialDiagnosesDisclaimer,
  PetProfileDiagnosesItem,
} from "@hospital/views/pets/petDiagnoses";
import { observer } from "mobx-react";
import React, { useMemo, useState } from "react";
import { FixedSizeList as List } from "react-window";

/**
 * Props for PetProfileDiagnosesList component.
 */
export type IPetProfileDiagnosesListProps = {
  query: string;
  petDiagnosesAdded: PetDiagnosisModel[];
  addPetDiagnosis: (diagnosis: DiagnosisModel) => void;
};

/**
 * Renders a searchable diagnoses table within the Pet Profile modal.
 * Displays search results, action buttons, and helpful empty states.
 */
export const PetProfileDiagnosesList = observer(
  ({
    query,
    petDiagnosesAdded,
    addPetDiagnosis,
  }: IPetProfileDiagnosesListProps) => {
    const {
      configuration: { diagnoses },
    } = useApp();
    const {diagnosisHistoryofPet} = usePetDetailsStore();

    const [showDuplicateDiagnosisWarning, setShowDuplicateDiagnosisWarning] =
      useState<boolean>(false);

    const [diagnosisToBeAdded, setDiagnosisToBeAdded] =
      useState<DiagnosisModel>({} as DiagnosisModel);

    /**
     * Filter the list of available diagnoses based on the search query.
     */
    const filteredDiagnoses = useMemo(() => {
      const searchTerm = (query ?? "").trim().toLowerCase();

      return StringUtility.hasValue(searchTerm)
        ? diagnoses.filter((d) => d.name.toLowerCase().includes(searchTerm))
        : [];
    }, [query, diagnoses]);

    const addPetDiagnosisToDiagnosisAddedToProfile = (
      diagnosis: DiagnosisModel,
      isExistingDiagnosis: boolean,
    ) => {
      setShowDuplicateDiagnosisWarning(isExistingDiagnosis);
      if (isExistingDiagnosis) {
        setDiagnosisToBeAdded({
          diagnosisId: diagnosis.diagnosisId,
          isActive: diagnosis.isActive,
          name: diagnosis.name,
          rowVersion: diagnosis.rowVersion
        });
      } else {
        addPetDiagnosis(diagnosis);
      }
    };
    return (
      <>
        {showDuplicateDiagnosisWarning && (
          <YesNoConfirmModal
            visible={showDuplicateDiagnosisWarning}
            busy={false}
            title={makeBold("Duplicate Diagnosis", undefined)}
            description="This pet already has this Diagnosis on file. Are you sure you want to continue?"
            onConfirm={() => {
              addPetDiagnosis(diagnosisToBeAdded);
              setShowDuplicateDiagnosisWarning(false);
            }}
            onCancel={() => setShowDuplicateDiagnosisWarning(false)}
          />
        )}
        <div className="d-flex flex-column gap-2 mt-2">
          {/* Header */}
          <div
            role="row"
            className="d-flex flex-row pet-profile-diagnoses-item-header"
          >
            <div className="flex-fill">
              <strong>DESCRIPTION</strong>
            </div>
            <div
              className="d-flex justify-content-end pe-4"
              style={{ width: "9rem" }}
            >
              <strong>ACTIONS</strong>
            </div>
          </div>

          {/* Disclaimer */}
          <PetProfileSearchInitialDiagnosesDisclaimer
            query={query}
            filteredDiagnoses={filteredDiagnoses}
          />

          {/* Diagnoses List */}
          {!_.isEmpty(filteredDiagnoses) && (
            <List
              height={280}
              itemCount={filteredDiagnoses.length}
              itemSize={40}
              width="100%"
            >
              {({ index, style }) => {
                const diagnosis = filteredDiagnoses[index];
                // Tracks diagnoses the user has added in the current modal session
                const alreadyAdded = petDiagnosesAdded.some(
                  (pd) => pd.diagnosisId === diagnosis.diagnosisId,
                );
                //Tracks diagnoses that already exist in the pet’s profile (from DB / previous visits)
                const isExistingDiagnosis = diagnosisHistoryofPet.some(
                  (diagnosisItem) =>
                    diagnosisItem.diagnosisId === diagnosis.diagnosisId,
                );

                return (
                  <PetProfileDiagnosesItem
                    diagnosesRow={diagnosis}
                    query={query}
                    style={style}
                    alreadyAdded={alreadyAdded}
                    addDiagnosis={(diagnosiss) =>
                      addPetDiagnosisToDiagnosisAddedToProfile(
                        diagnosiss,
                        isExistingDiagnosis,
                      )
                    }
                  />
                );
              }}
            </List>
          )}
        </div>
      </>
    );
  },
);
